'use client';

import { unstable_isUnrecognizedActionError } from 'next/navigation';
import * as Sentry from '@sentry/nextjs';
import { RELOAD_KEY } from '@/lib/deployment-skew-state';
import {
  FETCH_NO_RESPONSE,
  FETCH_NO_RESPONSE_MESSAGES,
  fetchNoResponseContext,
  recordSameOriginResponse,
  reportedFetchNoResponseErrors,
} from '@/lib/telemetry/fetch-no-response';
import { captureRouteError } from '@/lib/telemetry/route-error';
import { waitForStaticAssetReports } from '@/lib/telemetry/static-asset-errors';

type RecoveryOptions = { boundary?: string; isActive?: () => boolean };
let inFlight:
  { promise: Promise<boolean>; active: Set<() => boolean> } | undefined;

export function isDeploymentSkew(error: unknown): boolean {
  return (
    unstable_isUnrecognizedActionError(error) ||
    (error instanceof Error &&
      (error.name === 'ChunkLoadError' ||
        ('code' in error && error.code === 'CSS_CHUNK_LOAD_FAILED') ||
        /^Failed to load chunk .* (?:from module |as a runtime dependency of chunk )/.test(
          error.message
        ) ||
        /^Module .+ was instantiated because .+, but the module factory is not available\./.test(
          error.message
        )))
  );
}

export function recoverFromDeploymentSkew(
  error: unknown,
  reload = () => window.location.reload(),
  { boundary = 'deployment-skew', isActive = () => true }: RecoveryOptions = {}
): Promise<boolean> {
  if (!isDeploymentSkew(error)) {
    return Promise.resolve(false);
  }
  if (inFlight) {
    inFlight.active.add(isActive);
    return inFlight.promise;
  }
  const active = new Set([isActive]);
  const promise = performRecovery(error, reload, boundary, () =>
    [...active].some((check) => check())
  ).finally(() => {
    inFlight = undefined;
  });
  inFlight = { promise, active };
  return promise;
}

async function performRecovery(
  error: unknown,
  reload: () => void,
  boundary: string,
  isActive: () => boolean
): Promise<boolean> {
  const staleAction = unstable_isUnrecognizedActionError(error);
  if (error instanceof Error && !staleAction) {
    await captureRouteError(error, boundary);
  }
  await waitForStaticAssetReports();
  await Sentry.flush(2000);
  if (!isActive()) {
    return false;
  }

  // The key holds the build that last reloaded this tab, so each deploy gets
  // one reload and a skew that persists within one build cannot loop.
  const build = process.env.NEXT_PUBLIC_WAVE_GIT_COMMIT_SHA ?? '';
  try {
    if (sessionStorage.getItem(RELOAD_KEY) !== build) {
      sessionStorage.setItem(RELOAD_KEY, build);
      reload();
      return true;
    }
  } catch {
    // Storage may be unavailable; do not risk a reload loop.
  }
  if (error instanceof Error && staleAction) {
    await captureRouteError(error, boundary);
  }
  return false;
}

/** Keep a stale action out of the caller's ordinary error/toast path while the tab reloads. */
export async function callServerAction<Args extends unknown[], Result>(
  action: (...args: Args) => Promise<Result>,
  ...args: Args
): Promise<Result> {
  try {
    const result = await action(...args);
    recordSameOriginResponse();
    return result;
  } catch (error) {
    if (await recoverFromDeploymentSkew(error)) {
      return new Promise<never>(() => {});
    }
    // Match the browser's fetch rejection, not application TypeErrors or
    // failures from unrelated fetches such as the Cronofy authorization flow.
    if (
      error instanceof TypeError &&
      FETCH_NO_RESPONSE_MESSAGES.includes(error.message) &&
      !reportedFetchNoResponseErrors.has(error)
    ) {
      reportedFetchNoResponseErrors.add(error);
      const reportedError = new Error('Server Action → request failed');
      reportedError.name = FETCH_NO_RESPONSE;
      Sentry.captureException(reportedError, fetchNoResponseContext(error));
    }
    throw error;
  }
}
